import Button from "@/components/bricks/button/Button";
import { ReactNode, useReducer, useState } from "react";
import AlertRoot, { Alert } from "../../Alert/Alert";
import Form from "../../formcontrols/Form";
import EditIcon from "../../icons/EditIcon";
import Modal, { ModalRoot } from "../Modal";
import PlusIcon from "../../icons/PlusIcon";
import { RequireOnlyOne } from "../../../interfaces/ts-helpers";
export interface FormModalState {
  open: boolean;
  shouldWarnBeforeClosing: boolean;
  isSaving: boolean;
  warningOpen: boolean;
  isInvalidating: boolean;
}

interface FormModalPropsBase<U, V> {
  editConfig: FormConfig<U, V>;
  invalidateData?: InvalidateDataFunction<any>;
  method: FormMethod;
  title?: string;
  setMethod?: React.Dispatch<React.SetStateAction<FormMethod>>;
  triggerProps?: TriggerProps;
  triggerElement?: ReactNode;
}
type FormModalProps<U, V> = RequireOnlyOne<
  FormModalPropsBase<U, V>,
  "triggerProps" | "triggerElement"
>;

export default function FormModal<U, V = U>(props: FormModalProps<U, V>) {
  const {
    editConfig,
    invalidateData,
    method,
    setMethod,
    triggerProps,
    triggerElement,
    title,
  } = props;
  const [shouldReinit, setShouldReinit] = useState(false);

  const [formModalState, setFormModalState] = useReducer(
    (data: FormModalState, partialData: Partial<FormModalState>) => ({
      ...data,
      ...partialData,
    }),
    {
      open: false,
      shouldWarnBeforeClosing: false,
      warningOpen: false,
      isSaving: false,
      isInvalidating: false,
    },
  );

  return (
    <ModalRoot
      controller={{
        open: formModalState.open,
        setOpen: (open) => {
          if (formModalState.isSaving) {
            // don't close while loading
          } else if (formModalState.shouldWarnBeforeClosing) {
            setFormModalState({ warningOpen: true });
          } else {
            setFormModalState({ open });
          }
        },
      }}
    >
      <Modal
        title={
          <>
            {title ? (
              title
            ) : method === "patch" || method === "delete" ? (
              <>
                <EditIcon height={16} /> Redigér
              </>
            ) : (
              <>
                <PlusIcon height={22} /> Tilføj
              </>
            )}{" "}
            {editConfig.title}
          </>
        }
        description=""
      >
        <div className="c-modal-container">
          <Form
            config={editConfig}
            method={method}
            onSuccess={async () => {
              if (invalidateData) {
                setFormModalState({ isInvalidating: true });
                await invalidateData();
              }
              setFormModalState({ open: shouldReinit, isInvalidating: false });
            }}
            formModalState={{
              setShouldWarnBeforeClosing: (shouldWarnBeforeClosing) =>
                setFormModalState({ shouldWarnBeforeClosing }),
              setIsSaving: (isSaving) => setFormModalState({ isSaving }),
              shouldWarnBeforeClosing: formModalState.shouldWarnBeforeClosing,
              isSaving: formModalState.isSaving,
              open: formModalState.open,
              isInvalidating: formModalState.isInvalidating,
            }}
            setShouldReinit={setShouldReinit}
            setMethod={setMethod}
          />
        </div>
        <AlertRoot
          open={formModalState.warningOpen}
          setOpen={(open) => setFormModalState({ warningOpen: open })}
        >
          <Alert
            title="Er du sikker på at du vil lukke?"
            description="Der er ændringer i formularen der ikke er gemt."
            cancelButton={
              <Button color="secondary" onClick={() => {}}>
                Nej, gå tilbage til formularen
              </Button>
            }
            actionButton={
              <Button
                onClick={() => {
                  setFormModalState({
                    open: false,
                    shouldWarnBeforeClosing: false,
                    warningOpen: false,
                  });
                }}
              >
                Ja, luk
              </Button>
            }
          ></Alert>
        </AlertRoot>
      </Modal>
      {triggerProps && (
        <Button
          transparent={triggerProps.transparent}
          icon={triggerProps.icon}
          color={triggerProps.color}
          className={`tooltip-center ${triggerProps.className ?? ""}`}
          aria-label={triggerProps.title}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setFormModalState({ open: true });
          }}
        >
          {triggerProps.children}
        </Button>
      )}
      {triggerElement && <>{triggerElement}</>}
    </ModalRoot>
  );
}
type TriggerProps = {
  icon?: boolean;
  transparent?: boolean;
  className?: string;
  children: ReactNode;
  color: "primary" | "secondary";
  title: string;
  onClick?: (event?: React.MouseEvent) => void;
};
